import { useEffect, useState } from "react";
import Papa from "papaparse";

import { PageWrapper, PageWrapperProps } from "../../common-ui/PageWrapper";
import { useTranslation } from "react-i18next";
import { FileUploadButton } from "../../common-ui/FileUploadButton";
import { Alert, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { BulkInvitation, type BulkUploadState } from "./userInvitationUtils";
import { useAppSelector } from "../../app/hooks";
import { v4 as uuidv4 } from "uuid";
import { selectUserRoles } from "../Users/usersSlice";
import { Role } from "../Auth/roles";
import {
  useIdentifierTypes,
  useProgramsIdentifierConfig
} from "../Networks/networkUtils";
import {
  downloadBulkInviteTemplate,
  keepKnownBulkColumns,
  sysAdminTemplateIdentifiers,
  templateIdentifiersFromConfigs,
  type BulkTemplateContext
} from "./bulkInviteTemplate";
import { ErrorPage } from "../../common-ui/ErrorPage";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../Firebase/firebaseSlice";
import { getProgramsCurrentUserCanAdminister } from "../../app/utils";
import { useAllPrograms } from "../Programs/programUtils";
import { useTrainingStages } from "../TrainingStages/trainingStagesAPI";

const pageDetails: PageWrapperProps = {
  className: "UserInvitationsBulkUpload",
  headerText: "User Invitations - Bulk Upload"
};

export const UserInvitationsBulkUpload = () => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<BulkUploadState | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUserRoles = useAppSelector(selectUserRoles);
  const flags = useAppSelector(selectFeatureFlags);
  const flagsStatus = useAppSelector(selectFeatureFlagsStatus);
  const flagsError = useAppSelector(selectFeatureFlagsError);
  const { allow_bulk_invite } = flags;

  // default to true to avoid flashing the error screen when the flag is true
  const allowBulkInvite =
    flagsStatus === "succeeded" ? allow_bulk_invite : true;

  const isSysAdmin = currentUserRoles?.includes(Role.SystemAdmin);

  // SysAdmin -> SysAdmin template. Otherwise stage-based, precedence:
  // Fellowship -> Fellowship template; else Medical School (UME) -> MS template;
  // else Residency (GME) -> Residency template; fallback to the generic
  // ProgramAdmin template when no stage is known.
  const { programs: administeredPrograms } =
    getProgramsCurrentUserCanAdminister();
  // getProgramsCurrentUserCanAdminister() doesn't surface load status, so read
  // it from the underlying programs store directly.
  const { status: programsStatus } = useAllPrograms();
  const { trainingStages, status: trainingStagesStatus } = useTrainingStages();

  const stageCodeById = new Map(
    (trainingStages ?? []).map((s) => [s.id, s.code])
  );
  const administeredStageCodes = new Set(
    (administeredPrograms ?? [])
      .map((p) => stageCodeById.get(p.trainingStageId ?? ""))
      .filter((code): code is string => Boolean(code))
  );

  // Role/training-stage drives the base columns (mirrors the former stage-
  // specific templates: programId for SysAdmin, pgy/yearInStage per stage).
  const templateContext: BulkTemplateContext = isSysAdmin
    ? "sysadmin"
    : administeredStageCodes.has("FELLOWSHIP")
    ? "fellowship"
    : administeredStageCodes.has("UME")
    ? "medicalschool"
    : administeredStageCodes.has("GME")
    ? "residency"
    : "programadmin";

  // Identifier columns: SysAdmin gets all supported identifiers; a PA gets only
  // the identifiers their administered networks use. Fetch config for the PA's
  // programs only (empty list for SysAdmin → the hook makes no request).
  const administeredProgramIds = isSysAdmin
    ? []
    : (administeredPrograms ?? [])
        .map((p) => p.id)
        .filter((id): id is string => !!id);
  const { configs: identifierConfigs, status: identifierConfigStatus } =
    useProgramsIdentifierConfig(administeredProgramIds);

  // Every identifier type in the system: the SysAdmin template's columns, and
  // what tells identifier columns apart from other columns on upload.
  const {
    types: identifierTypes,
    status: identifierTypesStatus,
    retry: retryIdentifierTypes
  } = useIdentifierTypes();
  const identifierTypesLoaded = identifierTypesStatus === "succeeded";

  // Config is authoritative: a PA gets a column only for identifiers their
  // networks actually declare, with example values only for the roles that
  // require them. No legacy NPI-column fallback, so a network with no
  // identifiers configured yields no identifier columns — consistent with the
  // New/Edit User forms. A SysAdmin (no program context) gets every identifier.
  const templateIdentifiers = isSysAdmin
    ? sysAdminTemplateIdentifiers(identifierTypes)
    : templateIdentifiersFromConfigs(identifierConfigs, identifierTypes);

  // Only sysadmin can resolve synchronously; PA needs programs + stages + config.
  // The config gate is "settled" (succeeded OR failed), not "succeeded" only —
  // a transient config-fetch failure must not permanently disable the template
  // download (the button has no retry). On failure the template falls back to
  // whatever config resolved, and the server-side bulk validator stays the
  // authority on required identifiers.
  const identifierConfigSettled =
    identifierConfigStatus === "succeeded" ||
    identifierConfigStatus === "failed";
  const templateReady =
    (isSysAdmin && identifierTypesLoaded) ||
    (!isSysAdmin &&
      identifierTypesLoaded &&
      programsStatus === "succeeded" &&
      trainingStagesStatus === "succeeded" &&
      identifierConfigSettled);

  useEffect(() => {
    if (file && identifierTypesLoaded) {
      Papa.parse<BulkInvitation>(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim(),
        complete: (results) => {
          // Columns that are neither template nor identifier columns are
          // dropped; the review page warns about them.
          const { rows, ignoredColumns } = keepKnownBulkColumns(
            results.data as unknown as Record<string, unknown>[],
            identifierTypes
          );
          setParsedData({
            invitations: (rows as unknown as BulkInvitation[]).map((d) => ({
              ...d,
              programIdFromFile: d.programId,
              requestId: uuidv4()
            })),
            ignoredColumns
          });
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, identifierTypesLoaded]);

  useEffect(() => {
    if (parsedData) navigate("/invitations/bulkinvite", { state: parsedData });
  }, [parsedData]);

  if (!allowBulkInvite) {
    return <ErrorPage />;
  }

  return (
    <PageWrapper {...pageDetails}>
      <div>
        {t(
          "Upload a comma-delimited .csv file that includes the fields below for each user. A template file is provided for assistance."
        )}
        <ul>
          <li>{t("First Name")}</li>
          <li>{t("Last Name")}</li>
          <li>{t("Email Address")}</li>
          <li>
            {t(
              "Identifiers your network collects, such as NPI (if applicable; use the template's column names)"
            )}
          </li>
          <li>{t("Clinical Training Year in Program (if applicable)")}</li>
          <li>{t("Job Title (if applicable)")}</li>
          <li>
            {t(
              'Role (can be "Trainee", "Attending", or "ProgramAdmin" without quotes)'
            )}
          </li>
          <li>
            {t(
              'Trainee Type (if applicable, can be "Categorical", "DesignatedPreliminary", "NonDesignatedPreliminary", "Fellow", or "MedicalStudent" without quotes)'
            )}
          </li>
          {isSysAdmin && <li>{t("Program ID")}</li>}
        </ul>
        {t('Click "Upload" and select the completed file.')}
      </div>
      <div>
        <Button
          variant="outlined"
          disabled={!templateReady}
          onClick={() =>
            downloadBulkInviteTemplate(templateContext, templateIdentifiers)
          }
        >
          {t("Download Template")}
        </Button>
        <FileUploadButton setFile={setFile} disabled={!identifierTypesLoaded} />
      </div>
      {identifierTypesStatus === "failed" && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={retryIdentifierTypes}>
              {t("Try again")}
            </Button>
          }
        >
          {t("Couldn't load the identifier list needed for bulk upload.")}
        </Alert>
      )}
    </PageWrapper>
  );
};
